'use strict';

/**
 * <pre>
 * complement, noun
 *      1. A thing that completes or brings to perfection
 * </pre>
 */

const LOG = [];

function log(s)
{
    LOG[LOG.length] = s;
}

// ===========================================================================
// Host objects
// ===========================================================================

const ELEMENT = document.createElement('span'),

// Find the prototype of ELEMENT
ELEMENT_PROTOTYPE = (function(e)
{
    let last = null,
        prototype = Object.getPrototypeOf(e);
    while (prototype && 'removeAttribute' in prototype) {
        last = prototype;
        prototype = Object.getPrototypeOf(prototype);
    }
    return last;
})(ELEMENT),

TEXT_NODE = document.createTextNode('');

// ===========================================================================
// HTMLCollection
// ===========================================================================

/**
 * Creates a new |_HTMLCollection|.
 *
 * Note: Host |HTMLCollection| cannot be instantiated, so we need our own
 * implementation.
 *
 * @param {Array|NodeList} items the elements of the collection
 *
 * @see http://www.w3.org/TR/domcore/#interface-htmlcollection
 */
function _HTMLCollection(items)
{
    const length = items.length;
    Object.defineProperty(this, 'length', {
        value: length,
        writable: false
    });

    for (let i = 0; i < length; ++i) {
        const item = items[i];
        let key = item.id || item.name;
        this[i] = item;
        if (key) {
            key += '';
            if (key && typeof this[key] == 'undefined') {
                this[key] = item;
            }
        }
    }

    Object.freeze(this);
}

// Do not expose implementation details.
_HTMLCollection.toString = function()
{
    return '[object _HTMLCollection]';
};

_HTMLCollection.prototype = {
    constructor: _HTMLCollection,

    /**
     * @see http://www.w3.org/TR/domcore/#dom-htmlcollection-item
     */
    item: function(index)
    {
        return this[index] || null;
    },

    /**
     * @see http://www.w3.org/TR/domcore/#dom-htmlcollection-nameditem
     */
    namedItem: function(key)
    {
        key += '';
        if (!key) {
            return null;
        }
        const element = this[key];
        if (ELEMENT_PROTOTYPE.isPrototypeOf(element)) {
            return element;
        } else {
            for (let i = 0, n = this.length; i < n; ++i) {
                if (this[i].id === key || this[i].name === key) {
                    return this[i];
                }
            }
        }
        return null;
    },

    toString: function()
    {
        return _HTMLCollection.toString();
    }
};

/**
 * Complements a host object's prototype.
 *
 * @param {Object} object     a host object (e.g. Element)
 * @param {Object} properties extensions to the object's prototype
 * @param {Object} prototype  (optional) the object's prototype
 */
function complementHostPrototype(object, properties, prototype)
{
    if (!object) {
        return;
    }
    if (!prototype) {
        prototype = Object.getPrototypeOf(object);
    }
    for (const property in properties) {
        if (properties.hasOwnProperty(property) && typeof object[property] === 'undefined') {
            const descriptor = properties[property],
                  func = typeof descriptor === 'function';
            if (func) {
                prototype[property] = descriptor;
            } else {
                Object.defineProperty(prototype, property, {
                    get: descriptor.get,
                    set: descriptor.set,
                    enumerable: true,
                    configurable: true      // todo GT - false?
                });
            }
            log((prototype === ELEMENT_PROTOTYPE ? 'Element' : (object + '').split(' ', 2)[1].slice(0, -1)) + '.prototype.' + property + (func ? '()' : ''));
        }
    }
}

/**
 * Complements an HTML element's prototype.
 *
 * @param {String} elementName the element's name (e.g. "select")
 * @param {Object} properties  extensions to the element's prototype
 */
function complementHTMLElementPrototype(elementName, properties)
{
    let property, descriptor;
    const element = document.createElement(elementName);
    for (property in properties) {
        if (properties.hasOwnProperty(property) && typeof element[property] == 'undefined') {
            descriptor = properties[property];
            Object.defineProperty(element.constructor.prototype, property, {
                get: descriptor.get,
                set: descriptor.set,
                enumerable: true,
                configurable: true
            });

            (() => {
                let name = (element + '').split(' ', 2)[1].slice(0, -1);
                if (name == 'HTMLUnknownElement') {
                    name = 'HTML' + elementName.substr(0, 1).toUpperCase() + elementName.substr(1).toLowerCase() + 'Element';
                }
                log(name + '.prototype.' + property);
            })();
        }
    }
}

/**
 * @see http://dom.spec.whatwg.org/#parentnode
 */
const ParentNode = {
    firstElementChild: function(element)
    {
        element = element.firstChild;
        if (element) {
            return element.nodeType == 1 ? element : element.nextElementSibling;
        } else {
            return null;
        }
    },

    lastElementChild: function(element)
    {
        element = element.lastChild;
        if (element) {
            return element.nodeType == 1 ? element : element.previousElementSibling;
        } else {
            return null;
        }
    }
};

/**
 * @see http://dom.spec.whatwg.org/#nondocumenttypechildnode
 * @see http://dom.spec.whatwg.org/#childnode
 */
const NonDocumentTypeChildNode = {
    previousElementSibling: function(element)
    {
        while (element && (element = element.previousSibling) && element.nodeType != 1);
        return element;
    },

    nextElementSibling: function(element)
    {
        while (element && (element = element.nextSibling) && element.nodeType != 1);
        return element;
    }
};

const ChildNode = {
    remove: function(element) {
        if (element.parentNode) {
            element.parentNode.removeChild(element);
        }
    }
};

// ===================================================================
// Element.prototype
// ===================================================================

complementHostPrototype(ELEMENT, {
    /**
     * @see https://developer.mozilla.org/en-US/docs/Web/API/Element.matches
     */
    matches: ELEMENT_PROTOTYPE['msMatchesSelector']
          || ELEMENT_PROTOTYPE['mozMatchesSelector']
          || ELEMENT_PROTOTYPE['webkitMatchesSelector']
          || function(selector)
            {
                const element = this,
                      elements = (element.document || element.ownerDocument).querySelectorAll(selector);
                let i = 0;
                while (elements[i] && elements[i] !== element) {
                    i++;
                }
                return !!elements[i];
            },

    closest: ELEMENT_PROTOTYPE['closest']
          || function(selector)
            {
                let node = this;
                while (node && node.nodeType == 1) {
                    if (node.matches(selector)) {
                        return node;
                    } else {
                        node = node.parentNode;
                    }
                }
                return null;
            },

    dataset: {
        // Taken from: https://github.com/epiloque/element-dataset
        get: function()
        {
            // todo GT - use Proxy for catch-all getters and setters.

            const element = this,
                  map = {},
                  attributes = this.attributes;

            function getter()
            {
                return this.value;
            }

            function setter(name, value)
            {
                if (typeof value !== 'undefined') {
                    this.setAttribute(name, value);
                } else {
                    this.removeAttribute(name);
                }
            }

            for (let i = 0, n = attributes.length; i < n; ++i) {
                const attribute = attributes[i];
                if (attribute && attribute.name && (/^data-\w[\w\-]*$/).test(attribute.name)) {
                    const name = attribute.name,
                          value = attribute.value,
                          propertyName = name.substr(5).replace(/-./g, s => s.charAt(1).toUpperCase());
                    Object.defineProperty(map, propertyName, {
                        get: getter.bind({value: value || ''}),
                        set: setter.bind(element, name),
                        enumerable: true
                    });
                }
            }
            return map;
        }
    },

    remove: function() {
        ChildNode.remove(this);
    }
}, ELEMENT_PROTOTYPE);

// ===================================================================
// Text.prototype
// ===================================================================

complementHostPrototype(TEXT_NODE, {
    previousElementSibling: {
        get: function() {
            return NonDocumentTypeChildNode.previousElementSibling(this);
        }
    },

    nextElementSibling: {
        get: function() {
            return NonDocumentTypeChildNode.nextElementSibling(this);
        }
    },

    remove: function() {
        ChildNode.remove(this);
    }
});

// ===================================================================
// Document.prototype
// ===================================================================

complementHostPrototype(document, {
    firstElementChild: {
        get: function()
        {
            return ParentNode.firstElementChild(this);
        }
    },

    lastElementChild: {
        get: function()
        {
            return ParentNode.lastElementChild(this);
        }
    }
});

// ===================================================================
// HTMLSelectElement.prototype
// ===================================================================

complementHTMLElementPrototype('select', {
    /**
     * @see http://dev.w3.org/html5/spec-preview/the-select-element.html#dom-select-selectedoptions
     */
    selectedOptions: {
        get: function()
        {
            return new _HTMLCollection(this.querySelectorAll(':checked'));
        }
    }
});

complementHTMLElementPrototype('template', {
    /**
     * @see https://html.spec.whatwg.org/multipage/scripting.html#the-template-element
     */
    content: {
        get: function()
        {
            if (this.nodeName === 'TEMPLATE') {
                const fragment = document.createDocumentFragment();
                for (const child of this.childNodes) {
                    fragment.appendChild(child.cloneNode(true));
                }
                return fragment;
            } else {
                throw new Error('No such interface supported');
            }
        }
    }
});

// ===================================================================
// CustomEvent
// ===================================================================

(function () {
    try {
        new CustomEvent('test');
    } catch(e) {
        let CustomEvent = function (type, params)
            {
                params = params || {bubbles: false, cancelable: false, detail: undefined};

                let event = document.createEvent('CustomEvent');
                event.initCustomEvent(type, params.bubbles, params.cancelable, params.detail);

                let origPrevent = event.preventDefault;
                event.preventDefault = function () {
                    origPrevent.call(this);
                    try {
                        Object.defineProperty(this, 'defaultPrevented', {
                            get: function () {
                                return true;
                            }
                        });
                    } catch(e) {
                        this.defaultPrevented = true;
                    }
                };

                return event;
            };
        CustomEvent.prototype = window.Event.prototype;
        window.CustomEvent = CustomEvent;

        log('CustomEvent');
    }
})();
